import type { TFunction } from 'i18next'
import { DEFAULT_COUNTRY } from '@/lib/constants'
import { formatDate } from '@/lib/format'
import { formatPhoneNumber } from '@/lib/phone'
import type {
  EditTechnicianRequest,
  Technician,
  TechnicianCertification,
  TechnicianDetail,
  TechnicianRole,
  TechnicianWorkingHours,
} from '@/types'

export type TechnicianRolePreset = TechnicianRole
export type TechnicianSecondaryStatus = 'on_leave' | 'on_intervention'

export const DEFAULT_WORKING_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri']
export const DEFAULT_START_TIME = '08:00'
export const DEFAULT_END_TIME = '18:00'

export interface TechnicianFormValues {
  firstName: string
  lastName: string
  phone: string
  phone2: string
  email: string
  country: string
  city: string
  postalCode: string
  address: string
  certibiocideNo: string
  skills: Array<{ name: string; commission: string }>
  zones: string[]
  certifications: TechnicianCertification[]
  equipment: string[]
  vehicles: Array<{ name: string; plate: string }>
  workingDays: string[]
  startTime: string
  endTime: string
  breakStart: string
  breakEnd: string
  worksWeekends: boolean
  onLeave: boolean
  leaveFrom: string
  leaveTo: string
  interventionRadius: string
}

// Split a "HH:MM-HH:MM" break range into start/end; tolerant of empty/malformed.
export function parseBreakRange(value?: string | null): {
  start: string
  end: string
} {
  if (!value || typeof value !== 'string') return { start: '', end: '' }
  const [start = '', end = ''] = value.split('-')
  return { start: start.trim(), end: end.trim() }
}

export function technicianDetailToFormValues(
  tech: TechnicianDetail
): TechnicianFormValues {
  const parts = (tech.full_name ?? '').split(' ')
  const wh = tech.working_hours as TechnicianWorkingHours | null
  const brk = parseBreakRange(wh?.break)

  return {
    firstName: tech.first_name ?? parts[0] ?? '',
    lastName: tech.last_name ?? parts.slice(1).join(' '),
    phone: formatPhoneNumber(tech.phone),
    phone2: formatPhoneNumber(tech.phone_secondary),
    email: tech.email ?? '',
    country: tech.country ?? DEFAULT_COUNTRY,
    city: tech.city ?? '',
    postalCode: tech.postal_code ?? '',
    address: tech.address ?? '',
    certibiocideNo: tech.certibiocide_no ?? '',
    skills: tech.commission_rates
      ? Object.entries(tech.commission_rates).map(([name, commission]) => ({
          name,
          commission: String(commission ?? ''),
        }))
      : (tech.skills ?? []).map(s => ({ name: s, commission: '' })),
    zones: tech.zones ?? [],
    certifications: (tech.certifications ?? []).map(c => ({
      name: c.name,
      valid_until: c.valid_until ?? '',
    })),
    equipment: (tech.equipment ?? []).map(item => item.name),
    vehicles: (tech.vehicles ?? []).map(item => ({
      name: item.name ?? '',
      plate: item.plate ?? '',
    })),
    workingDays: wh?.days?.length
      ? fromApiWorkingDays(wh.days)
      : DEFAULT_WORKING_DAYS,
    startTime: wh?.start ?? DEFAULT_START_TIME,
    endTime: wh?.end ?? DEFAULT_END_TIME,
    breakStart: brk.start,
    breakEnd: brk.end,
    worksWeekends: tech.works_weekends ?? false,
    onLeave: tech.on_leave ?? false,
    leaveFrom: tech.leave_from ?? '',
    leaveTo: tech.leave_to ?? '',
    interventionRadius:
      tech.intervention_radius_km != null
        ? String(tech.intervention_radius_km)
        : '',
  }
}

export const TECHNICIAN_WEEK_DAYS_UI = [
  'Mon',
  'Tue',
  'Wed',
  'Thu',
  'Fri',
  'Sat',
  'Sun',
] as const

export type TechnicianWeekDayUi = (typeof TECHNICIAN_WEEK_DAYS_UI)[number]

export const TECHNICIAN_DAY_TO_API: Record<TechnicianWeekDayUi, string> = {
  Mon: 'monday',
  Tue: 'tuesday',
  Wed: 'wednesday',
  Thu: 'thursday',
  Fri: 'friday',
  Sat: 'saturday',
  Sun: 'sunday',
}

const API_TO_DAY = Object.fromEntries(
  Object.entries(TECHNICIAN_DAY_TO_API).map(([ui, api]) => [api, ui])
)

export function toApiWorkingDays(days: string[]): string[] {
  return days.map(
    d => TECHNICIAN_DAY_TO_API[d as TechnicianWeekDayUi] ?? d.toLowerCase()
  )
}

export function fromApiWorkingDays(days: string[]): string[] {
  return days.map(d => API_TO_DAY[d.toLowerCase()] ?? d)
}

/**
 * Whether a technician works the given weekday (0 = Mon … 6 = Sun), derived
 * from the canonical working_hours.days set — the same source as the profile
 * "Working days" line. A configured day (incl. Sat/Sun) always counts as a
 * working day; when no days are stored we fall back to the default Mon–Fri week.
 * This is the single source of truth for the availability grids so they stay in
 * sync with the technician's Working Hours.
 */
export function technicianWorksWeekday(
  workingHours:
    | TechnicianWorkingHours
    | Record<string, unknown>
    | null
    | undefined,
  weekdayIdx: number
): boolean {
  const days = (workingHours as { days?: unknown } | null | undefined)?.days
  if (!Array.isArray(days) || days.length === 0) {
    return weekdayIdx < 5
  }
  const workingSet = new Set(
    fromApiWorkingDays(days as string[]).map(d => d.toLowerCase())
  )
  const key = TECHNICIAN_WEEK_DAYS_UI[weekdayIdx]?.toLowerCase()
  return key ? workingSet.has(key) : false
}

export function getCommissionRates(
  tech: TechnicianDetail
): Record<string, number | string> {
  const wh = (tech.working_hours ?? {}) as Record<string, unknown>
  if (tech.commission_rates && typeof tech.commission_rates === 'object') {
    return tech.commission_rates
  }
  const legacy = wh.commissions as
    | { skill: string; commission: string }[]
    | undefined
  if (!legacy?.length) return {}
  return Object.fromEntries(
    legacy.map(c => [c.skill, c.commission]).filter(([skill]) => skill)
  )
}

// Technician role is persisted by the API. Legacy rows fall back to `hybrid`,
// matching the backend backfill and avoiding accidental over-restriction.
export function getTechnicianRolePreset(
  tech: Pick<Technician, 'role'>
): TechnicianRolePreset {
  return tech.role ?? 'hybrid'
}

export function technicianDetailToEditPayload(
  tech: TechnicianDetail
): EditTechnicianRequest {
  const parts = (tech.full_name ?? '').trim().split(/\s+/)
  const wh = tech.working_hours as TechnicianWorkingHours | null

  return {
    first_name: tech.first_name ?? parts[0] ?? '',
    last_name: tech.last_name ?? parts.slice(1).join(' '),
    status: tech.status,
    email: tech.email ?? '',
    role: tech.role ?? 'hybrid',
    phone: tech.phone ?? '',
    phone_secondary: tech.phone_secondary ?? undefined,
    country: tech.country ?? DEFAULT_COUNTRY,
    city: tech.city ?? '',
    postal_code: tech.postal_code ?? '',
    address: tech.address ?? '',
    certibiocide_no: tech.certibiocide_no ?? '',
    latitude: tech.latitude ?? undefined,
    longitude: tech.longitude ?? undefined,
    avatar_file_id: tech.avatar_file_id ?? null,
    skills: tech.skills ?? [],
    zones: tech.zones ?? [],
    certifications: (tech.certifications ?? []).map(cert => ({
      name: cert.name,
      valid_until: cert.valid_until ?? undefined,
    })),
    commission_rates:
      tech.commission_rates ??
      Object.fromEntries(
        Object.entries(getCommissionRates(tech))
          .map(([name, commission]) => [name, Number(commission)])
          .filter(([, commission]) => Number.isFinite(commission))
      ),
    equipment: (tech.equipment ?? []).map(item => ({
      name: item.name,
      operational: item.operational,
    })),
    vehicles: (tech.vehicles ?? []).map(item => ({
      name: item.name ?? '',
      plate: item.plate ?? undefined,
    })),
    works_weekends: tech.works_weekends ?? false,
    working_hours: wh
      ? {
          days: wh.days ?? [],
          start: wh.start ?? DEFAULT_START_TIME,
          end: wh.end ?? DEFAULT_END_TIME,
          break: wh.break ?? undefined,
        }
      : undefined,
    on_leave: tech.on_leave ?? false,
    leave_from: tech.leave_from ?? undefined,
    leave_to: tech.leave_to ?? undefined,
    intervention_radius_km: tech.intervention_radius_km ?? 0,
    block_in_schedule: tech.block_in_schedule ?? false,
    notes: tech.notes ?? undefined,
  }
}

type TechnicianStatusSource = {
  status?: string | null
  display_status?: string | null
  on_leave?: boolean | null
  leave_from?: string | null
  leave_to?: string | null
}

function toLocalDate(value: string | null | undefined): Date | null {
  if (!value) return null

  const [year, month, day] = value.split('-').map(Number)
  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day)
  ) {
    return null
  }

  return new Date(year, month - 1, day)
}

export function isTechnicianCurrentlyOnLeave(
  tech: TechnicianStatusSource,
  referenceDate: Date = new Date()
): boolean {
  const hasLeaveFlag =
    tech.on_leave ||
    tech.status === 'on_leave' ||
    tech.display_status === 'on_leave'

  if (!hasLeaveFlag) return false

  const currentDate = new Date(referenceDate)
  currentDate.setHours(0, 0, 0, 0)

  const leaveFrom = toLocalDate(tech.leave_from)
  if (leaveFrom && currentDate < leaveFrom) return false

  const leaveTo = toLocalDate(tech.leave_to)
  if (leaveTo && currentDate > leaveTo) return false

  return true
}

export function getTechnicianPrimaryStatus(
  tech: TechnicianStatusSource
): 'active' | 'inactive' {
  if (tech.display_status === 'on_intervention') return 'active'
  if (isTechnicianCurrentlyOnLeave(tech)) return 'inactive'
  if (tech.display_status === 'inactive') return 'inactive'
  if (tech.status === 'inactive') {
    return 'inactive'
  }
  return 'active'
}

export function getTechnicianSecondaryStatus(
  tech: TechnicianStatusSource
): TechnicianSecondaryStatus | null {
  if (isTechnicianCurrentlyOnLeave(tech)) return 'on_leave'
  if (tech.display_status === 'on_intervention') return 'on_intervention'
  return null
}

export function getTechnicianStatusLabel(
  tech: TechnicianStatusSource,
  t: TFunction
): string {
  return t(`enums:technicianStatus.${getTechnicianPrimaryStatus(tech)}`)
}

export function getTechnicianSecondaryStatusDetail(
  tech: TechnicianStatusSource,
  t: TFunction
): string | null {
  const secondaryStatus = getTechnicianSecondaryStatus(tech)
  if (secondaryStatus === 'on_leave') {
    const leaveTo = formatDate(tech.leave_to)
    if (leaveTo !== '-') {
      return t('list.statusInfo.onLeaveUntil', { date: leaveTo })
    }
    return t('availability.status.onLeaveDetail')
  }
  if (secondaryStatus === 'on_intervention') {
    return t('availability.status.onInterventionDetail')
  }
  return null
}

export function formatWorkingDays(
  days: string[] | undefined,
  t: TFunction
): string | null {
  if (!days?.length) return null
  return days
    .map(d =>
      t(`detail.weekdays.${String(d).toLowerCase()}`, { defaultValue: d })
    )
    .join(', ')
}

export function formatItemList(
  items: Array<string | { name?: string; plate?: string }> | null | undefined
): string | null {
  if (!items?.length) return null
  const formatted = items
    .map(item => {
      if (typeof item === 'string') return item
      const label = item.name ?? ''
      return item.plate ? `${label} (${item.plate})` : label
    })
    .filter(Boolean)
    .join(', ')
  return formatted || null
}
